import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import DocumentTitle from 'react-document-title';
import { toastr } from 'react-redux-toastr';
import useIntl from '../../hooks/useIntl';
import useHistory from '../../hooks/useHistory';
import {
  Stack,
  Box,
  Typography,
  SearchInput,
  Spinner,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { useMessagingHub, useIsNeedsHelpFilterShown } from './hooks';
import {
  selectedStatusesFromSearch,
  requestedConversationStatuses,
  matchesSelectedStatuses,
} from './statusFilters';
import MessagingHubList from './MessagingHubList';
import FilterBar from './components/FilterBar';
import ChatContainer from './ChatContainer';
import intlMessages from './messages';
import { parsePropertyIdFromPath } from '../../utils/navigation-helpers';
import useCustomEvent from '../../hooks/useCustomEvent';
import { CONVERSATION_STATUS_CHANGED_EVENT } from '../TextingTab/customEvents';
import { updateConversation } from './data';
import { useAppFlags } from '../../utils/featureFlags';

function MessagingHubContainer(): React$Node {
  const intl = useIntl();
  const history = useHistory();

  const { emit: emitConversationStatusChanged } = useCustomEvent({
    eventKey: CONVERSATION_STATUS_CHANGED_EVENT,
  });

  // Detect if we're in property context by checking the URL path
  const propertyIdFromPath = useMemo(() => {
    return parsePropertyIdFromPath(history.location.pathname);
  }, [history.location.pathname]);

  // Determine if we're at property level (scoped to a single property)
  const isPropertyLevel = Boolean(propertyIdFromPath);
  const isNeedsHelpFilterShown = useIsNeedsHelpFilterShown({ isPropertyLevel });
  const { leslie: leslieFlagEnabled } = useAppFlags();
  const isNeedsHelpBadgeShown = Boolean(
    leslieFlagEnabled && isNeedsHelpFilterShown,
  );

  // Read conversationId from URL query params
  const selectedConversationId = useMemo(() => {
    const params = new URLSearchParams(history.location.search);
    return params.get('conversationId');
  }, [history.location.search]);

  const pendingNewConversationRef = useRef(null);
  const previousSelectedConversationIdRef = useRef(selectedConversationId);
  const conversationsRef = useRef([]);
  const selectedConversationRef = useRef(null);
  const cachedConversationRef = useRef(null);

  const [searchValue, setSearchValue] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [filters, setFilters] = useState(() => ({
    statuses: selectedStatusesFromSearch(
      history.location.search,
      isNeedsHelpFilterShown,
    ),
    propertyIds: [],
    householdStatuses: [],
  }));

  const requestedStatuses = useMemo(
    () =>
      requestedConversationStatuses(filters.statuses, isNeedsHelpBadgeShown),
    [filters.statuses, isNeedsHelpBadgeShown],
  );

  const handleSearchSubmit = useCallback(
    (valueOverride) => {
      setSubmittedSearch(
        valueOverride !== undefined ? valueOverride : searchValue,
      );
    },
    [searchValue],
  );

  const handleSearchChange = useCallback(
    (value) => {
      setSearchValue(value);
      // Immediately trigger search when input is emptied
      if (value === '') {
        handleSearchSubmit('');
      }
    },
    [handleSearchSubmit],
  );

  // Determine effective property IDs for API calls
  // If at property level, always use the property from the path
  // Otherwise, use filter selections
  const effectivePropertyIds = useMemo(() => {
    if (isPropertyLevel && propertyIdFromPath) {
      return [propertyIdFromPath];
    }
    return filters.propertyIds.length > 0 ? filters.propertyIds : undefined;
  }, [isPropertyLevel, propertyIdFromPath, filters.propertyIds]);

  const {
    conversations: fetchedConversations,
    isLoading,
    isLoadingMore,
    loadMore,
    hasMore,
    refresh,
    updateConversationLocally,
  } = useMessagingHub({
    search: submittedSearch,
    statuses: requestedStatuses.length > 0 ? requestedStatuses : undefined,
    propertyIds: effectivePropertyIds,
    householdStatuses:
      filters.householdStatuses.length > 0
        ? filters.householdStatuses
        : undefined,
    forcePropertyScope: isPropertyLevel ? propertyIdFromPath : undefined,
  });

  const conversations = useMemo(
    () =>
      fetchedConversations.filter((conversation) =>
        matchesSelectedStatuses(
          conversation,
          filters.statuses,
          isNeedsHelpBadgeShown,
        ),
      ),
    [fetchedConversations, filters.statuses, isNeedsHelpBadgeShown],
  );

  const handleUpdateConversationLocally = useCallback(
    (conversationId, updates) => {
      cachedConversationRef.current = cachedConversationRef.current
        ? { ...cachedConversationRef.current, ...updates }
        : cachedConversationRef.current;

      if (
        updates.conversationStatus &&
        updates.conversationStatus !== 'NEW' &&
        pendingNewConversationRef.current?.conversationId === conversationId
      ) {
        pendingNewConversationRef.current = null;
      }

      updateConversationLocally(conversationId, updates);
    },
    [updateConversationLocally],
  );

  const handleSelectConversation = useCallback(
    (conversationId) => {
      const params = new URLSearchParams(history.location.search);
      if (conversationId) {
        params.set('conversationId', conversationId);

        const conv = conversationsRef.current.find(
          (c) => c.conversationId === conversationId,
        );
        if (conv && conv.conversationStatus !== 'NEW') {
          emitConversationStatusChanged();
        }
      } else {
        params.delete('conversationId');
      }
      history.replace({
        pathname: history.location.pathname,
        search: params.toString(),
      });
    },
    [history, emitConversationStatusChanged],
  );

  // Find the selected conversation from the list, falling back to cached
  // version so the chat panel survives filter/search changes
  const selectedConversationFromList = useMemo(
    () =>
      conversations.find(
        (conv) => conv.conversationId === selectedConversationId,
      ),
    [conversations, selectedConversationId],
  );

  if (selectedConversationFromList) {
    cachedConversationRef.current = selectedConversationFromList;
  }

  const selectedConversation =
    selectedConversationFromList ||
    (cachedConversationRef.current?.conversationId === selectedConversationId
      ? cachedConversationRef.current
      : undefined);

  // Keep refs in sync for use inside effects without adding object deps
  conversationsRef.current = conversations;
  selectedConversationRef.current = selectedConversation;

  // Defer NEW → UNRESOLVED: only update when another conversation is selected
  useEffect(() => {
    const previousSelectedId = previousSelectedConversationIdRef.current;

    // Only trigger when switching FROM one conversation TO another conversation
    if (
      previousSelectedId &&
      selectedConversationId &&
      previousSelectedId !== selectedConversationId
    ) {
      const pendingConversation = pendingNewConversationRef.current;
      if (
        pendingConversation &&
        pendingConversation.conversationId === previousSelectedId
      ) {
        // Only proceed if the conversation is still in NEW status.
        const currentConvInList = conversationsRef.current.find(
          (c) => c.conversationId === previousSelectedId,
        );
        const currentStatus =
          currentConvInList?.conversationStatus ??
          (cachedConversationRef.current?.conversationId === previousSelectedId
            ? cachedConversationRef.current?.conversationStatus
            : undefined);

        pendingNewConversationRef.current = null;

        if (currentStatus === 'NEW') {
          updateConversation(previousSelectedId, {
            conversationStatus: 'UNRESOLVED',
            propertyId: pendingConversation.propertyId,
          })
            .then(() => {
              handleUpdateConversationLocally(previousSelectedId, {
                conversationStatus: 'UNRESOLVED',
              });
              emitConversationStatusChanged();
            })
            .catch(() => {
              toastr.error(
                intl.formatMessage(intlMessages.errorHeader),
                intl.formatMessage(intlMessages.failedToUpdateStatusError),
              );
            });
        }
      }
    }

    // Track the current conversation's NEW status
    const currentConversation = selectedConversationRef.current;
    if (
      currentConversation &&
      currentConversation.conversationStatus === 'NEW'
    ) {
      pendingNewConversationRef.current = {
        conversationId: currentConversation.conversationId,
        propertyId: currentConversation.propertyId,
      };
    }

    previousSelectedConversationIdRef.current = selectedConversationId;
  }, [
    selectedConversationId,
    handleUpdateConversationLocally,
    emitConversationStatusChanged,
    intl,
  ]);

  return (
    <DocumentTitle title="Message Hub">
      <Stack
        p={3}
        sx={{
          backgroundColor: 'background.paper',
        }}
      >
        <Stack mb={3}>
          <Typography variant="titleLarge" gutterBottom>
            {intl.formatMessage(intlMessages.messagingHubTitle)}
          </Typography>
          <Typography variant="bodyMedium" color="text.secondary">
            {intl.formatMessage(intlMessages.messagingHubSubtitle)}
          </Typography>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <SearchInput
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={intl.formatMessage(intlMessages.searchPlaceholder)}
              onSubmitClick={handleSearchSubmit}
              fullWidth
              label={intl.formatMessage(intlMessages.search)}
            />
            {isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center">
                <Spinner />
              </Box>
            ) : (
              <MessagingHubList
                conversations={conversations}
                isLoadingMore={isLoadingMore}
                onLoadMore={loadMore}
                hasMore={hasMore}
                onRefresh={refresh}
                selectedConversationId={selectedConversationId}
                onSelectConversation={handleSelectConversation}
                isSearching={submittedSearch.length > 0}
                isNeedsHelpBadgeShown={isNeedsHelpBadgeShown}
              />
            )}
          </Grid>

          <Grid item xs={6} md={9}>
            <FilterBar
              onFilterChange={setFilters}
              isPropertyLevel={isPropertyLevel}
              propertyId={propertyIdFromPath}
              isNeedsHelpFilterShown={isNeedsHelpFilterShown}
            />
            <Box
              data-testid="messaging-hub-chat-panel"
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <ChatContainer
                selectedConversation={selectedConversation}
                onStatusChange={refresh}
                updateConversationLocally={handleUpdateConversationLocally}
              />
            </Box>
          </Grid>
        </Grid>
      </Stack>
    </DocumentTitle>
  );
}

export default MessagingHubContainer;
